/**
 * The browser half of "upload a logo": a File becomes a small pixel sample for
 * colour extraction and an upload the server will accept.
 *
 * radhotspotapi refuses a decoded logo over 150 KB, over 1600 px a side, or
 * anything but PNG or JPEG (SVG can carry script, and these bytes reach
 * anonymous guests). The upload is re-encoded here to fit, so an operator never
 * sees a refusal for a photo straight off a phone -- the server caps apply
 * regardless.
 */

export const MAX_UPLOAD_BYTES = 150 * 1024;
const SAMPLE_SIDE = 200;
const UPLOAD_WIDTH = 600;
const UPLOAD_HEIGHT = 400;

export interface LogoRead {
  sample: { data: Uint8ClampedArray; width: number; height: number };
  /** Base64 of the re-encoded upload. */
  base64: string;
  bytes: number;
  type: "image/png" | "image/jpeg";
  /** An object URL of the upload, for the preview. Revoke it when done. */
  previewUrl: string;
}

function draw(bitmap: ImageBitmap, maxW: number, maxH: number, opaque: boolean): HTMLCanvasElement {
  const scale = Math.min(1, maxW / bitmap.width, maxH / bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser cannot read images.");
  if (opaque) {
    // JPEG has no alpha: a transparent logo would otherwise turn black behind.
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas;
}

const toBlob = (c: HTMLCanvasElement, type: string, quality?: number) =>
  new Promise<Blob | null>((resolve) => c.toBlob(resolve, type, quality));

async function base64Of(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

export async function readLogo(file: File): Promise<LogoRead> {
  if (file.type !== "image/png" && file.type !== "image/jpeg") {
    throw new Error("Choose a PNG or JPEG logo. SVG and other formats are not accepted.");
  }
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error("This file could not be read as an image.");
  }
  try {
    const sampleCanvas = draw(bitmap, SAMPLE_SIDE, SAMPLE_SIDE, false);
    const px = sampleCanvas.getContext("2d")!.getImageData(0, 0, sampleCanvas.width, sampleCanvas.height);

    let maxW = UPLOAD_WIDTH;
    let maxH = UPLOAD_HEIGHT;
    let type: LogoRead["type"] = file.type;
    let blob: Blob | null = null;
    for (let attempt = 0; attempt < 6; attempt++) {
      if (type === "image/png") {
        blob = await toBlob(draw(bitmap, maxW, maxH, false), "image/png");
        if (blob && blob.size <= MAX_UPLOAD_BYTES) break;
        type = "image/jpeg";
      }
      const canvas = draw(bitmap, maxW, maxH, true);
      for (const q of [0.9, 0.8, 0.7, 0.6]) {
        blob = await toBlob(canvas, "image/jpeg", q);
        if (blob && blob.size <= MAX_UPLOAD_BYTES) break;
      }
      if (blob && blob.size <= MAX_UPLOAD_BYTES) break;
      maxW = Math.round(maxW * 0.75);
      maxH = Math.round(maxH * 0.75);
    }
    if (!blob || blob.size > MAX_UPLOAD_BYTES) {
      throw new Error("This logo could not be made smaller than 150 KB. Try a simpler image.");
    }
    return {
      sample: { data: px.data, width: px.width, height: px.height },
      base64: await base64Of(blob),
      bytes: blob.size,
      type,
      previewUrl: URL.createObjectURL(blob),
    };
  } finally {
    bitmap.close();
  }
}
